import {
  CheckCircleOutlined,
  CloudUploadOutlined,
  DeleteOutlined,
  EyeOutlined,
  HolderOutlined,
  PictureOutlined,
  StopOutlined,
} from '@ant-design/icons';
import { Button, Checkbox, Empty, Image, Radio, Skeleton, Tooltip, Upload } from 'antd';
import type { RcFile } from 'antd/es/upload';
import { useCallback, useMemo, useRef, useState } from 'react';
import { PageHeader } from '@/components/PageHeader/PageHeader';
import { useGlobalLoader } from '@/hooks/useGlobalLoader';
import { useNotify } from '@/hooks/useNotify';
import {
  useBulkDeleteMediaImagesMutation,
  useBulkUpdateMediaStatusMutation,
  useDeleteMediaImageMutation,
  useGetMediaImagesQuery,
  useReorderMediaImagesMutation,
  useUpdateMediaImageMutation,
  useUploadMediaImagesMutation,
} from '@/modules/media/mediaApi';
import type { MediaImage, MediaStatusFilter } from '@/types/media';
import { resolveMutationResult } from '@/utils/apiResponse';
import { confirmBulkDelete, confirmDelete } from '@/utils/confirmDialog';
import { getMediaImageUrl } from '@/utils/media';
import { reorderById } from '@/utils/reorder';
import styles from './Media.module.scss';

const ACCEPTED_TYPES = ['image/png', 'image/jpeg', 'image/webp', 'image/gif'];
const MAX_SIZE_BYTES = 5 * 1024 * 1024;

export default function Media() {
  const notify = useNotify();
  const [statusFilter, setStatusFilter] = useState<MediaStatusFilter>('all');
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [dragId, setDragId] = useState<string | null>(null);
  const [dragOverId, setDragOverId] = useState<string | null>(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewImage, setPreviewImage] = useState('');
  const uploadInputRef = useRef<HTMLInputElement>(null);

  const apiParams = useMemo(
    () => ({
      page: 1,
      limit: 500,
      sortBy: 'sortOrder',
      sortOrder: 'asc' as const,
      ...(statusFilter !== 'all' ? { status: statusFilter } : {}),
    }),
    [statusFilter],
  );

  const { data: listResult, isLoading, isFetching } = useGetMediaImagesQuery(apiParams);
  const [uploadMediaImages, { isLoading: isUploading }] = useUploadMediaImagesMutation();
  const [updateMediaImage] = useUpdateMediaImageMutation();
  const [deleteMediaImage] = useDeleteMediaImageMutation();
  const [reorderMediaImages] = useReorderMediaImagesMutation();
  const [bulkUpdateStatus, { isLoading: isBulkUpdating }] = useBulkUpdateMediaStatusMutation();
  const [bulkDelete, { isLoading: isBulkDeleting }] = useBulkDeleteMediaImagesMutation();

  useGlobalLoader(isFetching && !!listResult);
  useGlobalLoader(isUploading);

  const images = listResult?.results ?? [];
  const canDrag = statusFilter === 'all';
  const hasSelection = selectedIds.length > 0;
  const showSkeleton = isLoading && !listResult;

  const clearSelection = () => setSelectedIds([]);

  const toggleSelection = (id: string, checked: boolean) => {
    setSelectedIds((current) =>
      checked ? [...new Set([...current, id])] : current.filter((item) => item !== id),
    );
  };

  const uploadFiles = async (files: File[]) => {
    const validFiles = files.filter((file) => {
      if (!ACCEPTED_TYPES.includes(file.type)) {
        notify.error(`${file.name}: please upload PNG, JPG, WEBP, or GIF.`);
        return false;
      }

      if (file.size > MAX_SIZE_BYTES) {
        notify.error(`${file.name}: image must be 5MB or smaller.`);
        return false;
      }

      return true;
    });

    if (!validFiles.length) return;

    const formData = new FormData();
    validFiles.forEach((file) => formData.append('images', file));

    const result = await resolveMutationResult(uploadMediaImages(formData));
    if (result.ok) {
      notify.success(result.message ?? `${validFiles.length} image(s) uploaded.`);
      return;
    }

    notify.error(result.message);
  };

  const handleBeforeUpload = (file: RcFile, fileList: RcFile[]) => {
    const isLastFile = file.uid === fileList[fileList.length - 1]?.uid;
    if (isLastFile) {
      void uploadFiles(fileList);
    }
    return false;
  };

  const handleNativeUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files ?? []);
    event.target.value = '';
    if (!files.length) return;
    await uploadFiles(files);
  };

  const handleStatusToggle = useCallback(
    async (item: MediaImage) => {
      const result = await resolveMutationResult(
        updateMediaImage({ id: item.id, body: { isActive: !item.isActive } }),
      );

      if (result.ok) {
        notify.success(result.message ?? 'Status updated.');
        return;
      }

      notify.error(result.message);
    },
    [notify, updateMediaImage],
  );

  const handleDelete = useCallback(
    async (item: MediaImage) => {
      const confirmed = await confirmDelete('this image');
      if (!confirmed) return;

      const result = await resolveMutationResult(deleteMediaImage(item.id));
      if (result.ok) {
        notify.success(result.message ?? 'Image deleted.');
        setSelectedIds((keys) => keys.filter((key) => key !== item.id));
        return;
      }

      notify.error(result.message);
    },
    [deleteMediaImage, notify],
  );

  const handleBulkStatus = async (isActive: boolean) => {
    if (!selectedIds.length) return;

    const result = await resolveMutationResult(
      bulkUpdateStatus({ ids: selectedIds, isActive }),
    );

    if (result.ok) {
      notify.success(result.message ?? 'Images updated.');
      clearSelection();
      return;
    }

    notify.error(result.message);
  };

  const handleBulkDelete = async () => {
    if (!selectedIds.length) return;

    const confirmed = await confirmBulkDelete(selectedIds.length);
    if (!confirmed) return;

    const result = await resolveMutationResult(bulkDelete({ ids: selectedIds }));
    if (result.ok) {
      notify.success(result.message ?? 'Images deleted.');
      clearSelection();
      return;
    }

    notify.error(result.message);
  };

  const handleDrop = async (dropId: string) => {
    setDragOverId(null);

    if (!canDrag || !dragId || dragId === dropId) {
      setDragId(null);
      return;
    }

    const reordered = reorderById(images, dragId, dropId);
    setDragId(null);

    const result = await resolveMutationResult(
      reorderMediaImages({ ids: reordered.map((item) => item.id) }),
    );

    if (result.ok) {
      notify.success(result.message ?? 'Order updated.');
      return;
    }

    notify.error(result.message);
  };

  return (
    <div className={styles.page}>
      <PageHeader
        actions={
          <>
            <input
              ref={uploadInputRef}
              type="file"
              accept={ACCEPTED_TYPES.join(',')}
              multiple
              className={styles.hiddenInput}
              onChange={handleNativeUpload}
            />
            <Upload
              accept={ACCEPTED_TYPES.join(',')}
              multiple
              showUploadList={false}
              beforeUpload={handleBeforeUpload}
              disabled={isUploading}
            >
              <Button type="primary" icon={<CloudUploadOutlined />} loading={isUploading}>
                Upload images
              </Button>
            </Upload>
          </>
        }
      />

      <div className={styles.toolbar}>
        <Radio.Group
          value={statusFilter}
          onChange={(event) => {
            setStatusFilter(event.target.value);
            clearSelection();
          }}
          optionType="button"
          buttonStyle="solid"
          size="small"
        >
          <Radio.Button value="all">All</Radio.Button>
          <Radio.Button value="active">Active</Radio.Button>
          <Radio.Button value="inactive">Inactive</Radio.Button>
        </Radio.Group>
      </div>

      {hasSelection ? (
        <div className={styles.bulkBar}>
          <span className={styles.bulkCount}>{selectedIds.length} selected</span>
          <Button
            size="small"
            icon={<CheckCircleOutlined />}
            onClick={() => handleBulkStatus(true)}
            loading={isBulkUpdating}
          >
            Activate
          </Button>
          <Button
            size="small"
            icon={<StopOutlined />}
            onClick={() => handleBulkStatus(false)}
            loading={isBulkUpdating}
          >
            Deactivate
          </Button>
          <Button size="small" danger onClick={handleBulkDelete} loading={isBulkDeleting}>
            Delete
          </Button>
          <Button size="small" type="text" onClick={clearSelection}>
            Clear
          </Button>
        </div>
      ) : null}

      <div className={styles.gridCard}>
        {showSkeleton ? (
          <div className={styles.skeletonGrid}>
            {Array.from({ length: 8 }).map((_, index) => (
              <Skeleton.Image key={index} active className={styles.skeletonItem} />
            ))}
          </div>
        ) : images.length === 0 ? (
          <div className={styles.empty}>
            <PictureOutlined className={styles.emptyIcon} />
            <Empty description="No media images yet" image={Empty.PRESENTED_IMAGE_SIMPLE} />
            <Button
              type="primary"
              icon={<CloudUploadOutlined />}
              onClick={() => uploadInputRef.current?.click()}
              loading={isUploading}
            >
              Upload images
            </Button>
          </div>
        ) : (
          <div className={styles.grid}>
            {images.map((item) => {
              const imageSrc = getMediaImageUrl(item.imageLarge ?? item.imageSmall);
              const isSelected = selectedIds.includes(item.id);

              return (
                <div
                  key={item.id}
                  className={[
                    styles.item,
                    isSelected ? styles.itemSelected : '',
                    !item.isActive ? styles.itemInactive : '',
                    dragId === item.id ? styles.itemDragging : '',
                    dragOverId === item.id ? styles.draggingOver : '',
                  ]
                    .filter(Boolean)
                    .join(' ')}
                  draggable={canDrag}
                  onDragStart={() => setDragId(item.id)}
                  onDragEnd={() => {
                    setDragId(null);
                    setDragOverId(null);
                  }}
                  onDragOver={(event) => {
                    event.preventDefault();
                    setDragOverId(item.id);
                  }}
                  onDragLeave={() => setDragOverId(null)}
                  onDrop={() => handleDrop(item.id)}
                >
                  <div className={styles.imageWrap}>
                    {imageSrc ? (
                      <img src={imageSrc} alt="Media" className={styles.image} loading="lazy" />
                    ) : (
                      <div className={styles.empty}>
                        <PictureOutlined />
                      </div>
                    )}

                    <Checkbox
                      className={styles.checkbox}
                      checked={isSelected}
                      onChange={(event) => toggleSelection(item.id, event.target.checked)}
                      onClick={(event) => event.stopPropagation()}
                    />

                    {canDrag ? (
                      <button type="button" className={styles.dragHandle} aria-label="Drag to reorder">
                        <HolderOutlined />
                      </button>
                    ) : null}

                    <div className={styles.actions}>
                      <Tooltip title="View">
                        <Button
                          type="text"
                          size="small"
                          className={styles.actionBtn}
                          icon={<EyeOutlined />}
                          aria-label="View image"
                          onClick={() => {
                            if (!imageSrc) return;
                            setPreviewImage(imageSrc);
                            setPreviewOpen(true);
                          }}
                        />
                      </Tooltip>
                      <Tooltip title={item.isActive ? 'Deactivate' : 'Activate'}>
                        <Button
                          type="text"
                          size="small"
                          className={styles.actionBtn}
                          icon={item.isActive ? <StopOutlined /> : <CheckCircleOutlined />}
                          aria-label={item.isActive ? 'Deactivate image' : 'Activate image'}
                          onClick={() => handleStatusToggle(item)}
                        />
                      </Tooltip>
                      <Tooltip title="Delete">
                        <Button
                          type="text"
                          size="small"
                          className={`${styles.actionBtn} ${styles.actionBtnDanger}`}
                          icon={<DeleteOutlined />}
                          aria-label="Delete image"
                          onClick={() => handleDelete(item)}
                        />
                      </Tooltip>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <Image
        style={{ display: 'none' }}
        src={previewImage}
        preview={{
          visible: previewOpen,
          src: previewImage,
          onVisibleChange: (visible) => setPreviewOpen(visible),
        }}
      />
    </div>
  );
}
